import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, courseImageUrl, type Course, type CourseEdition, type Enrollment } from "@/lib/api";
import NuevoCursoModal from "@/components/feature/NuevoCursoModal";
import ListingFilters from "@/components/feature/ListingFilters";
import { emptyListingFilters, matchesMonth, type ListingFilterState } from "@/lib/listingFilters";

export default function CursosPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ListingFilterState>(emptyListingFilters);
  const [activeFilter, setActiveFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [editions, setEditions] = useState<CourseEdition[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesData, enrollmentsData, editionsData] = await Promise.all([
        api.getCourses({ includeDeleted: showDeleted }),
        api.getEnrollments(),
        api.getEditions(),
      ]);
      setCourses(coursesData);
      setEnrollments(enrollmentsData);
      setEditions(editionsData);
    } catch {
      setCourses([]);
      setEnrollments([]);
      setEditions([]);
    }
    setLoading(false);
  }, [showDeleted]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRestore = async (id: string) => {
    if (!confirm("¿Seguro que deseas recuperar este curso?")) return;
    await api.restoreCourse(id);
    loadData();
  };

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return courses.filter((c) => {
      if (filters.category && c.category !== filters.category) return false;
      if (filters.courseId && c.id !== filters.courseId) return false;
      if (filters.editionId) {
        const hasEdition = editions.some((ed) => ed.id === filters.editionId && ed.course_id === c.id);
        if (!hasEdition) return false;
      }
      if (filters.month) {
        const hasMonth = editions.some(
          (ed) => ed.course_id === c.id && matchesMonth(ed.start_date, filters.month)
        );
        if (!hasMonth) return false;
      }
      if (activeFilter === "activo" && !c.active) return false;
      if (activeFilter === "inactivo" && c.active) return false;
      if (q) {
        const haystack = [c.name, c.description, c.category].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [courses, editions, filters, activeFilter]);

  const getEnrollmentStats = (courseId: string) => {
    const courseEnrollments = enrollments.filter((e) => e.course_id === courseId);
    const total = courseEnrollments.length;
    if (total === 0) return { enrolled: 0, attRate: 0, compRate: 0 };
    const present = courseEnrollments.filter((e) => e.attendance_status === "presente").length;
    const completed = courseEnrollments.filter((e) => e.certificate_issued).length;
    return {
      enrolled: total,
      attRate: Math.round((present / total) * 100),
      compRate: Math.round((completed / total) * 100),
    };
  };

  const handleToggleLanding = async (course: Course) => {
    setTogglingId(course.id);
    try {
      const updated = await api.updateCourse(course.id, {
        show_on_landing: !course.show_on_landing,
      });
      setCourses((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    } catch {
      /* ignore */
    }
    setTogglingId(null);
  };

  const handleCreated = useCallback(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Cursos</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">Catálogo de cursos de capacitación</p>
        </div>
        <button onClick={() => setModalOpen(true)} className="btn-primary flex items-center gap-2 cursor-pointer">
          <i className="ri-add-line"></i>
          Nuevo Curso
        </button>
      </div>

      {!loading && (
        <ListingFilters
          title="Filtros de cursos"
          filters={filters}
          onChange={setFilters}
          courses={courses}
          editions={editions}
          showCourse={true}
          searchPlaceholder="Buscar cursos..."
          resultCount={filtered.length}
          totalCount={courses.length}
          hint="Mes filtra por fecha de inicio de alguna edición del curso."
          extraActive={Boolean(activeFilter || showDeleted)}
          onClearExtras={() => {
            setActiveFilter("");
            setShowDeleted(false);
          }}
          extras={
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">
                  Estado
                </label>
                <select
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                  className="input-field"
                  aria-label="Filtrar por estado"
                >
                  <option value="">Todos</option>
                  <option value="activo">Activos</option>
                  <option value="inactivo">Inactivos</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-[var(--color-text-muted)] cursor-pointer hover:text-[var(--color-text-primary)] transition-colors pb-2.5">
                  <input
                    type="checkbox"
                    className="rounded border-[var(--color-border)] bg-[var(--color-bg-primary)] text-[var(--color-accent)] focus:ring-[var(--color-accent)] cursor-pointer"
                    checked={showDeleted}
                    onChange={(e) => setShowDeleted(e.target.checked)}
                  />
                  Ver eliminados
                </label>
              </div>
            </>
          }
        />
      )}

      {loading ? (
        <div className="card text-center py-16">
          <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando cursos...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-16">
          <i className="ri-book-2-line text-4xl text-[var(--color-text-muted)]"></i>
          <p className="text-sm text-[var(--color-text-muted)] mt-3">No se encontraron cursos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((course) => {
            const { enrolled, attRate, compRate } = getEnrollmentStats(course.id);
            const editionCount = course._count?.editions ?? course.editions?.length ?? 0;

            return (
              <div
                key={course.id}
                className="card hover:border-[var(--color-border-light)] group"
              >
                <div className="flex gap-4 mb-4">
                  <img
                    src={courseImageUrl(course.image_url)}
                    alt={course.name}
                    className="w-20 h-20 rounded-xl object-cover flex-shrink-0 border border-[var(--color-border)]"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h3
                        onClick={() => navigate(`/cursos/${course.id}`)}
                        className="text-sm font-semibold text-[var(--color-text-primary)] truncate cursor-pointer hover:text-[var(--color-text-accent)]"
                      >
                        {course.name}
                      </h3>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {course.deleted_at ? (
                          <span className="badge-danger bg-red-500/20 text-red-500">Eliminado</span>
                        ) : course.active ? (
                          <span className="badge-success">Activo</span>
                        ) : (
                          <span className="badge-warning">Inactivo</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] mt-1 line-clamp-2">
                      {course.description}
                    </p>
                    <span className="badge-info text-[10px] mt-2 inline-block">{course.category}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs">
                  <div>
                    <span className="text-[var(--color-text-muted)]">Duración:</span>
                    <p className="text-[var(--color-text-primary)] font-medium">{course.duration}</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">Horas:</span>
                    <p className="text-[var(--color-text-primary)] font-medium">{course.hours}h</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">Ediciones:</span>
                    <p className="text-[var(--color-text-primary)] font-medium">{editionCount}</p>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">Inscritos:</span>
                    <p className="text-[var(--color-text-accent)] font-semibold">{enrolled}</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-[var(--color-text-primary)]">{enrolled}</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Estudiantes</p>
                  </div>
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-[var(--color-text-accent)]">{attRate}%</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Asistencia</p>
                  </div>
                  <div className="bg-[var(--color-bg-tertiary)] rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-green-400">{compRate}%</p>
                    <p className="text-[10px] text-[var(--color-text-muted)]">Completado</p>
                  </div>
                </div>

                <div className="border-t border-[var(--color-border)] pt-4 flex items-center justify-between">
                  <label className={`flex items-center gap-2 text-xs text-[var(--color-text-secondary)] ${course.deleted_at ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      checked={course.show_on_landing}
                      disabled={togglingId === course.id || !!course.deleted_at}
                      onChange={() => handleToggleLanding(course)}
                      className={course.deleted_at ? 'cursor-not-allowed' : 'cursor-pointer'}
                    />
                    Mostrar en landing
                  </label>
                  {course.deleted_at ? (
                    <button
                      onClick={() => handleRestore(course.id)}
                      className="text-xs text-green-500 hover:text-green-400 font-semibold inline-flex items-center gap-1 cursor-pointer"
                    >
                      <i className="ri-refresh-line"></i> Recuperar
                    </button>
                  ) : (
                    <button
                      onClick={() => navigate(`/cursos/${course.id}`)}
                      className="text-xs text-[var(--color-text-accent)] group-hover:translate-x-1 transition-transform inline-flex items-center gap-1 cursor-pointer"
                    >
                      Ver detalle <i className="ri-arrow-right-s-line"></i>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <NuevoCursoModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onCreated={handleCreated} />
    </div>
  );
}
